import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import type {
  IssueParticipantRecoveryCodeInput,
  IssueParticipantRecoveryCodeResult,
  ListParticipantsInput,
  ListParticipantsResult,
  ParticipantSummary,
  UpdateParticipantInput,
  UpdateParticipantResult,
} from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * ユーザー管理 (地区 > ユーザー管理) の client hooks (2026-09-07)。
 *
 * 参加者の登録データ (`participants/{uid}/memberships/{districtId}`) は firestore.rules では本人しか
 * 書けないため、一覧取得も修正もすべて callable 経由 (Admin SDK) で行う。
 * `useBulkPhotoImport` と同じ idle/loading/success/error の状態機械。
 */

export type ParticipantsState =
  | { status: 'idle'; participants: []; error: null }
  | { status: 'loading'; participants: []; error: null }
  | { status: 'ready'; participants: ParticipantSummary[]; error: null }
  | { status: 'error'; participants: []; error: Error };

export interface UseParticipantsAdminReturn {
  state: ParticipantsState;
  /** 一覧を取得 (保存後の再取得にも使う)。 */
  load: (districtId: string) => Promise<ParticipantSummary[] | null>;
  /** 登録データを修正 (同一人物の全端末に反映される)。 */
  update: (input: UpdateParticipantInput) => Promise<UpdateParticipantResult>;
  /** 引き継ぎコードを再発行 (平文コードはこの戻り値でだけ受け取れる)。 */
  issueRecoveryCode: (
    input: IssueParticipantRecoveryCodeInput,
  ) => Promise<IssueParticipantRecoveryCodeResult>;
  saving: boolean;
}

export function useParticipantsAdmin(): UseParticipantsAdminReturn {
  const [state, setState] = useState<ParticipantsState>({
    status: 'idle',
    participants: [],
    error: null,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (districtId: string): Promise<ParticipantSummary[] | null> => {
    setState({ status: 'loading', participants: [], error: null });
    try {
      const fn = httpsCallable<ListParticipantsInput, ListParticipantsResult>(
        functions,
        'listParticipants',
      );
      const res = await fn({ districtId });
      const participants = res.data.participants ?? [];
      setState({ status: 'ready', participants, error: null });
      return participants;
    } catch (e) {
      setState({ status: 'error', participants: [], error: e as Error });
      return null;
    }
  }, []);

  const update = useCallback(async (input: UpdateParticipantInput) => {
    setSaving(true);
    try {
      const fn = httpsCallable<UpdateParticipantInput, UpdateParticipantResult>(
        functions,
        'updateParticipant',
      );
      const res = await fn(input);
      return res.data;
    } finally {
      setSaving(false);
    }
  }, []);

  const issueRecoveryCode = useCallback(async (input: IssueParticipantRecoveryCodeInput) => {
    setSaving(true);
    try {
      const fn = httpsCallable<
        IssueParticipantRecoveryCodeInput,
        IssueParticipantRecoveryCodeResult
      >(functions, 'issueParticipantRecoveryCode');
      const res = await fn(input);
      return res.data;
    } finally {
      setSaving(false);
    }
  }, []);

  return { state, load, update, issueRecoveryCode, saving };
}
